import type { FC } from "react";
import { useMemo, useState } from "react";

import { trackRedeemCheckout } from "@loyalty/utils";
import dompurify from "isomorphic-dompurify";

import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { SupportingText } from "@jti/core-components/components/SupportingText";
import { toast } from "@jti/core-components/components/Toaster";
import { translateBackendErrorCode } from "@jti/fe-common";
import { getUser, LoyaltyApi } from "@jti/rest-api";
import { type BuyPrizeElementResponseType, type GetPrizesElementResponseType } from "@jti/rest-api-types";
import { useDispatch } from "@jti/store/reduxHooks";
import { fetchUserPoints } from "@jti/store/slices/loyaltyCoreSlice";
import { Dialog } from "@jti/ui/components/Dialog";
import { Typography } from "@jti/ui/components/Typography";
import { useCurrentBreakpoint } from "@jti/ui/hooks/useCurrentBreakpoint";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { replaceStringPlaceholder } from "@jti/ui/utils/replaceStringPlaceholder";

import { DialogCard } from "./DialogCard";
import { RedeemDialogButtons } from "./RedeemDialogButtons";

import styles from "./RedeemDialog.module.scss";

const { sanitize } = dompurify;

type CodesResponse = {
  codes: BuyPrizeElementResponseType["codes"];
  type: string;
};

export type RedeemDialogLabels = {
  costLabel: string;
  availablePointsLabel: string;
  balanceAfterRedemptionLabel: string;
  primaryCta: string;
  secondaryCta: string;
  dialogTitle: string;
  dialogTitleConfirmation: string;
  bottomText: string;
  pointsLabel: string;
  confirmationMessage: string;
  confirmationMessagePlural: string;
  codeLabel: string;
  codeInfo: string;
  unitLabel: string;
  emailLabel?: string;
  emailHint?: string;
  externalCodeButtonLabel?: string;
  quantityAriaLabel?: string;
  increaseAriaLabel?: string;
  decreaseAriaLabel?: string;
};
export type PrizeData = GetPrizesElementResponseType | Record<string, never>;

export type RedeemDialogProps = {
  isOpened: boolean;
  pointsBalance: number;
  prizeData: PrizeData;
  onCloseDialog: () => void;
  labels: RedeemDialogLabels;
};

export enum DIALOG_STEP {
  SUMMARY = 0,
  CONFIRMATION = 1,
}

export const RedeemDialog: FC<RedeemDialogProps> = ({ isOpened, prizeData, pointsBalance, onCloseDialog, labels }) => {
  const [dialogStep, setDialogStep] = useState(DIALOG_STEP.SUMMARY);
  const [quantity, setQuantity] = useState(1);
  const [{ codes, type }, setRewardCodes] = useState<CodesResponse>({ codes: [], type: "" });
  const [isQueryInProgress, setIsQueryInProgress] = useState(false);
  const dispatch = useDispatch();
  const { __, dictionary } = useTranslations();
  const {
    confirmationMessage,
    confirmationMessagePlural,
    primaryCta,
    secondaryCta,
    costLabel,
    availablePointsLabel,
    balanceAfterRedemptionLabel,
    emailLabel,
    emailHint,
    codeLabel,
    codeInfo,
    externalCodeButtonLabel,
    unitLabel,
    pointsLabel,
    increaseAriaLabel,
    decreaseAriaLabel,
    quantityAriaLabel,
  } = labels;

  const { mainBreakpoint } = useCurrentBreakpoint();

  const isSmallScreen = mainBreakpoint === "xs";

  const confirmationLabel = useMemo(() => {
    const message = quantity > 1 ? confirmationMessagePlural : confirmationMessage;
    if (!prizeData.points || !prizeData.name) return message;
    const placeholders = [
      {
        placeholder: "qty",
        value: quantity,
      },
      {
        placeholder: "redeemedPoints",
        value: prizeData.points * quantity,
      },
      {
        placeholder: "voucherName",
        value: __(prizeData.name),
      },
    ];
    return replaceStringPlaceholder(message, placeholders);
  }, [quantity, confirmationMessage, confirmationMessagePlural, prizeData.points, prizeData.name]);

  if (!prizeData?.prize_uuid) return null;

  const handleCloseDialog = () => {
    onCloseDialog();
    setQuantity(1);
    setDialogStep(DIALOG_STEP.SUMMARY);
  };

  const buyPrize = async () => {
    const user = getUser();
    if (user?.ciam_token && user.uuid) {
      setIsQueryInProgress(true);
      LoyaltyApi.init()
        .buyPrize(user.uuid, prizeData.prize_uuid, quantity, user.ciam_token)
        .then((response) => {
          dispatch(fetchUserPoints({ useLocalCache: false, dispatchUpdateEvent: true }));
          setRewardCodes({ codes: response.codes, type: response.codes_type ?? "" });
          setDialogStep(DIALOG_STEP.CONFIRMATION);
        })
        .catch((error) => {
          const preparedError = translateBackendErrorCode(error?.error_code, __(error.message), dictionary);
          return toast.error(preparedError);
        })
        .finally(() => setIsQueryInProgress(false));
    } else {
      toast.error(__("mobile.error.session_timeout"));
    }
  };

  const handleRedeemPrize = () => {
    buyPrize();
    trackRedeemCheckout(prizeData);
  };

  return (
    <Dialog isOpened={isOpened} onClose={handleCloseDialog} closable={!isQueryInProgress} fullscreen={isSmallScreen}>
      <div className={styles.container} data-step={dialogStep}>
        <Typography variant="h2" weight="light" dataTestId="redeemDialogSummaryTitle" className={styles.title}>
          {dialogStep === DIALOG_STEP.SUMMARY ? labels.dialogTitle : labels.dialogTitleConfirmation}
        </Typography>
        {dialogStep === DIALOG_STEP.CONFIRMATION && (
          <div className={styles.messageBox}>
            <AlertBanner
              tone="success"
              richDescription={<p dangerouslySetInnerHTML={{ __html: sanitize(confirmationLabel) }} />}
            />
          </div>
        )}
        <div className={styles.imageHolder}>
          <img src={prizeData.images[1]} alt="" data-testid="redeemDialogImage" />
        </div>
        <DialogCard
          pointsBalance={pointsBalance}
          labels={{
            costLabel,
            pointsLabel,
            availablePointsLabel,
            balanceAfterRedemptionLabel,
            emailLabel,
            emailHint,
            codeLabel,
            codeInfo,
            externalCodeButtonLabel,
            unitLabel,
            quantityAriaLabel,
            increaseAriaLabel,
            decreaseAriaLabel,
          }}
          prizeData={prizeData}
          dialogStep={dialogStep}
          codes={codes}
          type={type}
          quantity={{ val: quantity, set: setQuantity }}
        />
        {dialogStep === DIALOG_STEP.SUMMARY && (
          <>
            <RedeemDialogButtons
              primaryCtaLabel={primaryCta}
              secondaryCtaLabel={secondaryCta}
              handleCloseDialog={handleCloseDialog}
              handleRedeemPrize={handleRedeemPrize}
              isQueryInProgress={isQueryInProgress}
            />
            <div className={styles.bottomText}>
              <SupportingText>
                <span dangerouslySetInnerHTML={{ __html: sanitize(labels.bottomText) }} />
              </SupportingText>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
